import { prefersReducedMotion } from '@theme/utilities';

/**
 * The corner badge on a group block, when the merchant has asked it to animate in.
 *
 * Everything visual lives in snippets/group.liquid: the badge is rendered small and transparent and
 * this element's only job is to set `data-grow="in"` once the badge has scrolled into view, which
 * starts the keyframes there.
 *
 * The badge is only rendered as <group-badge> when the animation is switched on — a static badge
 * stays a plain div and never loads this module.
 */
class GroupBadge extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    // The pre-animation state is declared inside `prefers-reduced-motion: no-preference`, so with
    // motion reduced the badge is already at full size and there is nothing to reveal.
    if (prefersReducedMotion()) return;

    // Once per badge. A section re-render in the theme editor builds a fresh element and replays
    // the grow, which is the preview the merchant wants; a DOM move (the slideshow loop relocating
    // a slide) reconnects this same element, and that shouldn't pop the badge a second time.
    if (this.dataset.grow === 'in') return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        // The first callback reports the current state whether or not it intersects, so a badge
        // already on screen at load animates immediately and one below the fold waits.
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#observer?.disconnect();
        this.#observer = null;
        this.dataset.grow = 'in';
      },
      {
        // Observing the badge rather than its block: the badge overhangs the block's corner, so
        // this fires as the corner it is pinned to comes into view, which is the moment the grow is
        // meant to be seen. A fifth of it is enough to commit — the badge is small, and waiting for
        // more of it means a badge pinned to a tall block's bottom edge never quite qualifies.
        threshold: 0.2,

        // Hold off until the badge is properly inside the viewport rather than grazing the bottom
        // edge, so the animation isn't half over by the time it is worth looking at.
        rootMargin: '0px 0px -5% 0px',
      }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('group-badge')) {
  customElements.define('group-badge', GroupBadge);
}
